import { useCallback, useMemo, useState } from 'react';

import { ASK_DICTIONARY_WITH_DEFINITION } from 'approot/dictionary/dictionary.graphql';
import {
  ActivityBuilderDrawerActions,
  ActivityBuilderDrawerContent,
  ActivityBuilderDrawerHeader,
  ActivityBuilderDrawerWrapper,
} from 'approot/activity-builder/activity-builder-drawer';
import { useVocabularySettingContext } from './vocabulary-setting.context';
import InlineServerError from 'approot/errors/inline-server-error';
import { useLoggingQuery } from 'lib/apollo/apollo-hooks';
import { Box } from 'approot/shared/design-system/box/box.style';
import {
  DictionaryDefinition,
  FormDefinition,
  FormExample,
  SOURCE_AI,
  VOCABULARY_SETTINGS,
  VOCABULARY_SETTINGS_ASK_AI_FOR_NEW_DEFINITION,
  FormWordsTranslation,
} from './vocabulary-setting.constants';
import { LANGUAGE_CODE_EN_US, LanguageCodes } from 'global/constants';
import { PrimaryButton } from 'approot/shared/styles/buttons';
import type {
  AskDictionaryWithDefinitionQuery,
  AskDictionaryWithDefinitionQueryVariables,
} from '../../../../dictionary/dictionary.graphql.generated';
import { ActivityBuilderDrawerForm } from 'approot/activity-builder/activity-builder-drawer.style';
import { VocabularySettingForm } from './vocabulary-setting-form';
import customNanoId from 'lib/nanoid/nanoid';
import {
  assignTranslations,
  collectSentences,
  getFieldErrors,
  validateForm,
} from './vocabulary-setting-form.utils';
import {
  useSaveToDictionaryBook,
  useTranslateTo,
} from './vocabulary-setting.hooks';

type Props = {
  componentId: string;
  sectionId: string;
  vocabularyIds: number[];
  suggestion: DictionaryDefinition;
  goTo: (id: number) => void;
};

const VocabularySettingAskAIForDefinition = ({
  componentId,
  sectionId,
  vocabularyIds,
  suggestion,
  goTo,
}: Props) => {
  const { word, setFormErrors, setWord } = useVocabularySettingContext();

  const [wordsTranslation, setWordsTranslation] = useState<
    FormWordsTranslation[]
  >([]);
  const [definitions, setDefs] = useState<FormDefinition[]>([]);
  const [examples, setExamples] = useState<FormExample[]>([]);

  const { data, loading, error, called } = useLoggingQuery<
    AskDictionaryWithDefinitionQuery,
    AskDictionaryWithDefinitionQueryVariables
  >(ASK_DICTIONARY_WITH_DEFINITION, {
    fetchPolicy: 'no-cache',
    variables: {
      input: {
        dictionaryId: suggestion.id,
        word: suggestion.word,
        definition: suggestion.sourceDefinition || '',
      },
    },
    onCompleted: res => {
      const data = res.askDictionaryWithDefinition;
      setDefs(
        data.definitions.map(def => ({
          id: customNanoId(),
          description: def.definition || '',
          year: def.year,
          language: LANGUAGE_CODE_EN_US,
        }))
      );

      setExamples(
        data.examples.map(item => ({
          exampleId: item.exampleId || customNanoId(),
          example: item.example,
          language: LANGUAGE_CODE_EN_US,
          isGeneratingImage: !item.image,
          image: item.image
            ? {
                alt: item.image.alt || undefined,
                key: item.image.key,
                imageUrl: item.image.responsive?.[0].urls?.[0].url,
                original: item.image.original || undefined,
                cropData: item.image.cropJson,
                fileName: item.image.fileName,
                designId: undefined,
              }
            : undefined,
        }))
      );
    },
  });

  const result = data?.askDictionaryWithDefinition;
  const isGeneratingImages = useMemo(
    () => examples.some(ex => ex.isGeneratingImage),
    [examples]
  );

  const { saveToDictionary, loadingSaveToDictionary, loadingUpdateComponent } =
    useSaveToDictionaryBook({
      shouldUpdateComponent: true,
      componentId,
      sectionId,
      vocabularyIds,
      setWord,
      goTo,
    });

  const onSubmit = useCallback(() => {
    saveToDictionary({
      definition: suggestion,
      wordsTranslation: [
        {
          language: LANGUAGE_CODE_EN_US,
          word: suggestion.word.trim(),
        },
      ],
      completionId: result?.completionId,
      lexical: suggestion.lexical || '',
      examples,
      definitions,
    });
  }, [
    saveToDictionary,
    definitions,
    examples,
    result?.completionId,
    suggestion,
  ]);

  const {
    translateTo,
    loading: loadingTranslation,
    error: translateToError,
  } = useTranslateTo();

  const handleAddTranslation = useCallback(
    (language: LanguageCodes) => {
      translateTo({
        variables: {
          input: {
            sentences: collectSentences(
              definitions,
              examples,
              wordsTranslation
            ),
            language,
          },
        },
      }).then(res => {
        const {
          definitions: updatedDefinitions,
          examples: updatedExamples,
          wordsTranslation: updatedWordsTranslation,
        } = assignTranslations({
          definitions,
          examples,
          translations: res.data?.translateTo.translations || [],
          language,
          wordsTranslation: wordsTranslation,
        });
        setWordsTranslation([...updatedWordsTranslation]);
        setDefs([...updatedDefinitions]);
        setExamples([...updatedExamples]);
      });
    },
    [definitions, examples, translateTo, wordsTranslation]
  );

  const isFormValid = useCallback(() => {
    const {
      hasError,
      definitions: updatedDefinitions,
      examples: updatedExamples,
    } = validateForm(definitions, examples);

    setDefs([...updatedDefinitions]);
    setExamples([...updatedExamples]);

    if (hasError) {
      setFormErrors(getFieldErrors(definitions, examples, wordsTranslation));
    }

    return !hasError;
  }, [definitions, examples, setFormErrors, wordsTranslation]);

  return (
    <ActivityBuilderDrawerForm>
      <ActivityBuilderDrawerWrapper>
        <ActivityBuilderDrawerHeader
          isBack
          title={suggestion.word}
          onClose={() => {
            if (suggestion.source === SOURCE_AI) {
              goTo(VOCABULARY_SETTINGS_ASK_AI_FOR_NEW_DEFINITION);
            } else {
              goTo(VOCABULARY_SETTINGS);
            }
          }}
        />
        <ActivityBuilderDrawerContent>
          {error && (
            <Box $marginBottom={24}>
              <InlineServerError customError="Something went wrong, please go back and try again" />
            </Box>
          )}
          {!error && (
            <VocabularySettingForm
              isSavedAndApproved={false}
              isLoading={loading || definitions.length === 0}
              isLoadingLanguage={loadingTranslation}
              hasTranslateError={!!translateToError}
              wordsTranslation={wordsTranslation}
              definitions={definitions}
              examples={examples}
              word={word}
              lexicalCategory={suggestion.lexical || 'noun'}
              shouldGenerateImages={called}
              updateDefinitions={setDefs}
              updateExamples={examples => setExamples(examples)}
              handleAddTranslation={handleAddTranslation}
              updateWordsTranslation={setWordsTranslation}
            />
          )}
        </ActivityBuilderDrawerContent>
        <ActivityBuilderDrawerActions>
          <PrimaryButton
            type="button"
            loading={loadingSaveToDictionary || loadingUpdateComponent}
            disabled={
              loading ||
              loadingSaveToDictionary ||
              loadingUpdateComponent ||
              isGeneratingImages
            }
            onClick={() => {
              if (isFormValid()) {
                onSubmit();
              }
            }}
            data-selector="btn-save-and-approve"
          >
            Save and Approve
          </PrimaryButton>
        </ActivityBuilderDrawerActions>
      </ActivityBuilderDrawerWrapper>
    </ActivityBuilderDrawerForm>
  );
};

export default VocabularySettingAskAIForDefinition;
